import { useState, useEffect, type ReactNode } from "react";
import { Routes, Route, Navigate, useNavigate, useParams } from "react-router-dom";
import { IMO, buildFormSchemaForCarriers, isFieldVisible, SIGNING_DOCUMENT_DETAILS } from "@aam/shared";
import { AgentForm } from "./components/AgentForm";
import { GoogleLogin } from "./components/GoogleLogin";
import { SubmissionStatus } from "./components/SubmissionStatus";
import { Dashboard } from "./components/Dashboard";
import { RunArtifacts } from "./components/RunArtifacts";
import { SmsUplineManagement } from "./components/SmsUplineManagement";
import { AuthenticatedLayout, useAuthenticatedAgent } from "./components/AuthenticatedLayout";
import { AgentsList } from "./components/AgentsList";
import { UsersList } from "./components/UsersList";
import { StaffAgentDetail } from "./components/agent-detail/StaffAgentDetail";
import { ProviderAgentDetail } from "./components/agent-detail/ProviderAgentDetail";
import { PortalSetupPage } from "./components/portal/PortalSetupPage";
import { PortalContractsPage } from "./components/portal/PortalContractsPage";
import { PortalDownlineChangeRequestPage } from "./components/portal/PortalDownlineChangeRequestPage";
import { PortalDownlineContractsPage } from "./components/portal/PortalDownlineContractsPage";
import { SurelcSsoPage } from "./components/SurelcSsoPage";
import { PortalApplicationPage } from "./components/portal/PortalApplicationPage";
import { PortalServicePage } from "./components/portal/PortalServicePage";
import { PortalDocumentSigningPage } from "./components/portal/PortalDocumentSigningPage";
import { ServicesOps } from "./components/dashboard/ServicesOps";
import { FeatureFlags } from "./components/dashboard/FeatureFlags";
import { CarrierManagement } from "./components/dashboard/CarrierManagement";
import { AdminInbox } from "./components/dashboard/AdminInbox";
import { OnboardingTasks } from "./components/dashboard/OnboardingTasks";
import { normalizeDraft } from "./lib/normalizeDraft";
import { useFeatureFlags } from "./hooks/useFeatureFlags";
import {
  applicationCompletePath,
  APPLICATION_COMPLETE_STEP,
  clearStoredSubmissionId,
  getStoredSubmissionId,
  storeSubmissionId,
} from "./lib/submissionStorage";
import { DownlinesList } from "./components/DownlinesList";
import { AccountPage } from "./components/AccountPage";
import { AgentRequestQueue } from "./components/AgentRequestQueue";
import { DownlineChangeRequestQueue } from "./components/DownlineChangeRequestQueue";
import { CarrierChangeRequestQueue } from "./components/CarrierChangeRequestQueue";
import { MarketingHomePage } from "./components/MarketingHomePage";
import { SyncHealth } from "./components/SyncHealth";
import { ReferralSignupPage } from "./components/ReferralSignupPage";
import { PageBody } from "./components/ui/PageHeader";
import { PublicShell } from "./context/ThemeContext";
import { LoadingSpinner } from "./components/LoadingSpinner";
import {
  useSession,
  getStoredReferralCode,
  clearStoredReferralCode,
} from "./hooks/useSession";
import { loadDraft } from "./hooks/useDraft";
import { useFormStore } from "./store/formStore";
import { useToast } from "./context/ToastContext";
import { useDocumentTitle } from "./hooks/useDocumentTitle";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:4000";

function DashboardMain() {
  useDocumentTitle("Dashboard");
  return (
    <main className="max-w-[1400px] mx-auto px-4 py-8">
      <Dashboard />
    </main>
  );
}

function AgentsMain() {
  useDocumentTitle("Agents");
  const agent = useAuthenticatedAgent();
  if (agent.role === "AGENT") return <Navigate to="/my-agent" replace />;
  return (
    <main className="max-w-[1400px] mx-auto px-4 py-8">
      <AgentsList viewerRole={agent.role} providerImo={agent.providerImo} />
    </main>
  );
}

function UsersMain() {
  useDocumentTitle("Users");
  const agent = useAuthenticatedAgent();
  if (agent.role !== "ADMIN") return <Navigate to="/dashboard/agents" replace />;
  return (
    <main className="max-w-[1400px] mx-auto px-4 py-8">
      <UsersList />
    </main>
  );
}

function ServicesOpsMain() {
  useDocumentTitle("Services");
  const agent = useAuthenticatedAgent();
  if (agent.role !== "ADMIN") return <Navigate to="/dashboard/agents" replace />;
  return (
    <PageBody>
      <ServicesOps />
    </PageBody>
  );
}

function FeatureFlagsMain() {
  useDocumentTitle("Feature Flags");
  const agent = useAuthenticatedAgent();
  if (agent.role !== "ADMIN") return <Navigate to="/dashboard/agents" replace />;
  return (
    <PageBody>
      <FeatureFlags />
    </PageBody>
  );
}

function CarriersMain() {
  useDocumentTitle("Carriers");
  const agent = useAuthenticatedAgent();
  if (agent.role !== "ADMIN") return <Navigate to="/dashboard/agents" replace />;
  return (
    <PageBody>
      <CarrierManagement />
    </PageBody>
  );
}

function InboxMain() {
  useDocumentTitle("Inbox");
  const agent = useAuthenticatedAgent();
  if (agent.role !== "ADMIN") return <Navigate to="/dashboard/agents" replace />;
  return (
    <PageBody>
      <AdminInbox />
    </PageBody>
  );
}

function OnboardingTasksMain() {
  useDocumentTitle("Onboarding tasks");
  const agent = useAuthenticatedAgent();
  if (agent.role !== "ADMIN") return <Navigate to="/dashboard/agents" replace />;
  return (
    <PageBody>
      <OnboardingTasks />
    </PageBody>
  );
}

function ContractsFeatureGate({ children }: { children: ReactNode }) {
  const { flags, loading } = useFeatureFlags();
  if (loading) return <LoadingSpinner />;
  if (!flags.contracts) return <Navigate to="/my-agent" replace />;
  return <>{children}</>;
}

function AgentDetailMain() {
  useDocumentTitle("Agent Detail");
  const agent = useAuthenticatedAgent();
  if (agent.role === "AGENT") return <Navigate to="/my-agent" replace />;
  return (
    <main className="max-w-[1400px] mx-auto px-4 py-8">
      {agent.role === "ADMIN" ? (
        <StaffAgentDetail viewerUserId={agent.id} />
      ) : agent.role === "PROVIDER" || agent.role === "PPG" ? (
        <ProviderAgentDetail />
      ) : (
        <Navigate to="/dashboard/agents" replace />
      )}
    </main>
  );
}

function MyAgentMain() {
  useDocumentTitle("Setup");
  const agent = useAuthenticatedAgent();
  if (agent.isStaff) return <Navigate to="/dashboard/agents" replace />;
  if (agent.role !== "AGENT") return <Navigate to="/login" replace />;
  return (
    <PageBody>
      <PortalSetupPage />
    </PageBody>
  );
}

function MyAgentContractsMain() {
  useDocumentTitle("Contracts");
  const agent = useAuthenticatedAgent();
  if (agent.isStaff) return <Navigate to="/dashboard/agents" replace />;
  if (agent.role !== "AGENT") return <Navigate to="/login" replace />;
  return (
    <ContractsFeatureGate>
      <PageBody>
        <PortalContractsPage />
      </PageBody>
    </ContractsFeatureGate>
  );
}

function MyAgentServiceMain({ product }: { product: "DIALER_INTELELEAD" | "INSURANCE_TOOLKIT" }) {
  useDocumentTitle(product === "DIALER_INTELELEAD" ? "Dialer Fee" : "Insurance Toolkit");
  const agent = useAuthenticatedAgent();
  if (agent.isStaff) return <Navigate to="/dashboard/agents" replace />;
  if (agent.role !== "AGENT") return <Navigate to="/login" replace />;
  return (
    <PageBody>
      <PortalServicePage product={product} />
    </PageBody>
  );
}

function MyAgentDocumentSigningMain({ documentType }: { documentType: "ACH_FORM" | "CC_AUTH_FORM" }) {
  useDocumentTitle(SIGNING_DOCUMENT_DETAILS[documentType].label);
  const agent = useAuthenticatedAgent();
  if (agent.isStaff) return <Navigate to="/dashboard/agents" replace />;
  if (agent.role !== "AGENT") return <Navigate to="/login" replace />;
  return (
    <PageBody>
      <PortalDocumentSigningPage documentType={documentType} />
    </PageBody>
  );
}

function MyAgentApplyMain() {
  useDocumentTitle("Contract Request");
  const agent = useAuthenticatedAgent();
  if (agent.isStaff) return <Navigate to="/dashboard/agents" replace />;
  if (agent.role !== "AGENT") return <Navigate to="/login" replace />;
  return (
    <ContractsFeatureGate>
      <PortalApplicationPage />
    </ContractsFeatureGate>
  );
}

function MyAgentDownlinesMain() {
  useDocumentTitle("Downlines");
  const agent = useAuthenticatedAgent();
  if (agent.isStaff) return <Navigate to="/dashboard/agents" replace />;
  if (agent.role !== "AGENT") return <Navigate to="/login" replace />;
  if (!agent.canManageDownlines) return <Navigate to="/my-agent" replace />;
  return (
    <PageBody constrained>
      <DownlinesList />
    </PageBody>
  );
}

function MyAgentDownlineChangeRequestMain() {
  useDocumentTitle("Change request");
  const agent = useAuthenticatedAgent();
  if (agent.isStaff) return <Navigate to="/dashboard/agents" replace />;
  if (agent.role !== "AGENT") return <Navigate to="/login" replace />;
  if (!agent.canManageDownlines) return <Navigate to="/my-agent" replace />;
  return (
    <PageBody>
      <PortalDownlineChangeRequestPage />
    </PageBody>
  );
}

function MyAgentDownlineContractsMain() {
  useDocumentTitle("Downline contracts");
  const agent = useAuthenticatedAgent();
  if (agent.isStaff) return <Navigate to="/dashboard/agents" replace />;
  if (agent.role !== "AGENT") return <Navigate to="/login" replace />;
  if (!agent.canManageDownlines) return <Navigate to="/my-agent" replace />;
  return (
    <PageBody>
      <PortalDownlineContractsPage />
    </PageBody>
  );
}

function MyAgentAccountMain() {
  useDocumentTitle("Account");
  const agent = useAuthenticatedAgent();
  if (agent.isStaff) return <Navigate to="/dashboard/account" replace />;
  if (agent.role !== "AGENT") return <Navigate to="/login" replace />;
  return (
    <PageBody constrained>
      <AccountPage shell="portal" />
    </PageBody>
  );
}

function StaffAccountMain() {
  useDocumentTitle("Account");
  return (
    <PageBody constrained>
      <AccountPage shell="staff" />
    </PageBody>
  );
}

function CarrierChangeRequestQueueMain() {
  useDocumentTitle("Carrier Change Requests");
  const agent = useAuthenticatedAgent();
  if (agent.role !== "ADMIN" && agent.role !== "PROVIDER" && agent.role !== "PPG") {
    return <Navigate to="/dashboard/agents" replace />;
  }
  return (
    <main className="max-w-[1400px] mx-auto px-4 py-8">
      <CarrierChangeRequestQueue />
    </main>
  );
}

function AgentRequestQueueMain() {
  useDocumentTitle("Agent Requests");
  const agent = useAuthenticatedAgent();
  if (agent.role !== "ADMIN") return <Navigate to="/dashboard/agents" replace />;
  return (
    <main className="max-w-[1400px] mx-auto space-y-8 px-4 py-8">
      <AgentRequestQueue />
      <DownlineChangeRequestQueue />
    </main>
  );
}

function StaffOnlyMain({ children }: { children: ReactNode }) {
  const agent = useAuthenticatedAgent();
  if (agent.role === "AGENT") return <Navigate to="/my-agent" replace />;
  if (agent.role === "PPG") return <Navigate to="/dashboard/agents" replace />;
  return <>{children}</>;
}

function SmsUplinesMain() {
  useDocumentTitle("SMS Uplines");
  return (
    <main className="max-w-[1400px] mx-auto px-4 py-8">
      <SmsUplineManagement />
    </main>
  );
}

function RunArtifactsMain() {
  useDocumentTitle("Run Artifacts");
  return <RunArtifacts />;
}

function SyncHealthMain() {
  useDocumentTitle("Sync Health");
  return (
    <main className="max-w-[1400px] mx-auto px-4 py-8">
      <SyncHealth />
    </main>
  );
}

function stepSlugsFor(data: Record<string, unknown>) {
  const carriers = Array.isArray(data.selectedCarriers) ? data.selectedCarriers.map(String) : [];
  return buildFormSchemaForCarriers(carriers).sections.map((section) => section.id.replace(/_/g, "-"));
}

function stepToSlug(step: number, data: Record<string, unknown>) {
  const slugs = stepSlugsFor(data);
  return slugs[step] ?? slugs[0];
}

function slugToStep(slug: string, data: Record<string, unknown>) {
  const idx = stepSlugsFor(data).indexOf(slug);
  return idx >= 0 ? idx : 0;
}

async function submitForm(
  data: Record<string, unknown>,
  encryptedFields: string[] = [],
  referralCode?: string
): Promise<string> {
  const carriers = (data.selectedCarriers as string[]) ?? [];
  const activeFieldIds = new Set(
    buildFormSchemaForCarriers(carriers).sections.flatMap((section) =>
      section.fields
        .filter((field) => isFieldVisible(field, data))
        .map((field) => field.id)
    )
  );
  const safeData = Object.fromEntries(
    Object.entries(data).filter(([key, value]) => activeFieldIds.has(key) && !(value instanceof File))
  );
  const activeEncryptedFields = encryptedFields.filter((fieldId) => activeFieldIds.has(fieldId));
  const res = await fetch(`${API_BASE}/api/submissions`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      carriers,
      formData: safeData,
      encryptedFields: activeEncryptedFields,
      referralCode: referralCode ?? getStoredReferralCode() ?? undefined,
    }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: "Submission failed" }));
    const error = new Error(err.error || "Submission failed") as Error & {
      fieldErrors?: Array<{ label: string }>;
    };
    error.fieldErrors = Array.isArray(err.fieldErrors) ? err.fieldErrors : undefined;
    throw error;
  }
  const json = await res.json();
  return json.data.id as string;
}

function ResumeFormPage() {
  useDocumentTitle("Contract Request");
  const { token, step } = useParams<{ token: string; step: string }>();
  const navigate = useNavigate();
  const updateMultipleFields = useFormStore((s) => s.updateMultipleFields);
  const encryptedFields = useFormStore((s) => s.encryptedFields);
  const { error: toastError } = useToast();

  const [ready, setReady] = useState(false);
  const [initialStep, setInitialStep] = useState(0);
  const [submissionId, setSubmissionId] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      navigate("/", { replace: true });
      return;
    }

    const storedSubmissionId = getStoredSubmissionId(token);
    if (step === APPLICATION_COMPLETE_STEP) {
      if (storedSubmissionId) {
        setSubmissionId(storedSubmissionId);
        setReady(true);
        return;
      }
      toastError("That contract request link has expired. Starting a new one.");
      navigate("/", { replace: true });
      return;
    }

    loadDraft(token).then((draft) => {
      if (!draft) {
        if (storedSubmissionId) {
          setSubmissionId(storedSubmissionId);
          navigate(applicationCompletePath(token), { replace: true });
          setReady(true);
          return;
        }
        toastError("That contract request link has expired. Starting a new one.");
        navigate("/", { replace: true });
        return;
      }
      const normalized = normalizeDraft({
        formData: draft.formData as Record<string, unknown>,
        currentStep: draft.currentStep,
      });
      updateMultipleFields(normalized.formData);
      const urlStep = step
        ? slugToStep(step, normalized.formData)
        : normalized.currentStep;
      setInitialStep(urlStep);
      setReady(true);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, step]);

  if (!ready) return <LoadingSpinner />;

  if (submissionId) {
    return (
      <SubmissionStatus
        submissionId={submissionId}
        onReturn={() => {
          if (token) clearStoredSubmissionId(token);
          useFormStore.getState().resetForm();
          navigate("/", { replace: true });
        }}
      />
    );
  }

  return (
    <AgentForm
      imo={IMO.PINNACLE}
      initialStep={initialStep}
      draftToken={token}
      onStepChange={(nextStep) => {
        navigate(`/agent-form/${token}/${stepToSlug(nextStep, useFormStore.getState().formData)}`, { replace: true });
      }}
      onDraftCreated={(newToken, nextStep) => {
        navigate(`/agent-form/${newToken}/${stepToSlug(nextStep, useFormStore.getState().formData)}`);
      }}
      onSubmit={async (data) => {
        try {
          const id = await submitForm(data, encryptedFields, getStoredReferralCode() ?? undefined);
          clearStoredReferralCode();
          storeSubmissionId(token!, id);
          setSubmissionId(id);
          navigate(applicationCompletePath(token!), { replace: true });
        } catch (err) {
          toastError(err instanceof Error ? err.message : "Submission failed. Please try again.");
          throw err;
        }
      }}
      onBack={() => navigate("/", { replace: true })}
    />
  );
}

function LoginPage() {
  useDocumentTitle("Log In");
  const { agent, loading } = useSession();
  const inviteToken = new URLSearchParams(window.location.search).get("invite")?.trim() ?? "";
  if (!loading && agent) {
    if (agent.isStaff) {
      return <Navigate to="/dashboard/agents" replace />;
    }
    if (agent.role === "AGENT") {
      return <Navigate to="/my-agent" replace />;
    }
  }
  return (
    <PublicShell shell={inviteToken ? "staff" : "portal"}>
      <GoogleLogin />
    </PublicShell>
  );
}

function PPGAgentsRedirect() {
  return <Navigate to="/dashboard/agents" replace />;
}

function LegacyRegisterRedirect() {
  const { referralCode } = useParams<{ referralCode: string }>();
  return <Navigate to={`/join/${encodeURIComponent(referralCode ?? "")}`} replace />;
}

export default function App() {
  return (
    <div className="min-h-screen">
      <Routes>
        <Route path="/" element={<PublicShell shell="portal"><MarketingHomePage /></PublicShell>} />
        <Route
          path="/join/:referralCode"
          element={
            <PublicShell shell="portal">
              <ReferralSignupPage />
            </PublicShell>
          }
        />
        <Route path="/register/:referralCode" element={<LegacyRegisterRedirect />} />
        <Route path="/agent-form/:token/:step" element={<ResumeFormPage />} />
        <Route path="/login" element={<LoginPage />} />

        <Route element={<AuthenticatedLayout />}>
          <Route path="/surelc/sso" element={<SurelcSsoPage />} />
          <Route path="/my-agent" element={<MyAgentMain />} />
          <Route path="/my-agent/contracts" element={<MyAgentContractsMain />} />
          <Route path="/my-agent/contracts/apply" element={<MyAgentApplyMain />} />
          <Route path="/my-agent/contracts/apply/:token" element={<MyAgentApplyMain />} />
          <Route path="/my-agent/services/dialer" element={<MyAgentServiceMain product="DIALER_INTELELEAD" />} />
          <Route path="/my-agent/setup/training" element={<Navigate to="/my-agent?step=training" replace />} />
          <Route path="/my-agent/setup/desktop-app" element={<Navigate to="/my-agent?step=desktopApp" replace />} />
          <Route path="/my-agent/services/insurance-toolkit" element={<MyAgentServiceMain product="INSURANCE_TOOLKIT" />} />
          <Route path="/my-agent/documents/ach-form" element={<MyAgentDocumentSigningMain documentType="ACH_FORM" />} />
          <Route path="/my-agent/documents/cc-auth-form" element={<MyAgentDocumentSigningMain documentType="CC_AUTH_FORM" />} />
          <Route path="/my-agent/downlines" element={<MyAgentDownlinesMain />} />
          <Route path="/my-agent/change-request" element={<MyAgentDownlineChangeRequestMain />} />
          {/* The change request lived under /downlines before it had its own nav item. */}
          <Route
            path="/my-agent/downlines/change-request"
            element={<Navigate to="/my-agent/change-request" replace />}
          />
          <Route path="/my-agent/downlines/:downlineId" element={<MyAgentDownlineContractsMain />} />
          <Route path="/my-agent/account" element={<MyAgentAccountMain />} />
          {/* Dashboard list temporarily hidden — redirect staff to agents.
          <Route
            path="/dashboard"
            element={
              <StaffOnlyMain>
                <DashboardMain />
              </StaffOnlyMain>
            }
          />
          */}
          <Route path="/dashboard" element={<Navigate to="/dashboard/agents" replace />} />
          <Route path="/dashboard/agents" element={<AgentsMain />} />
          <Route path="/dashboard/agents/:agentId" element={<AgentDetailMain />} />
          <Route path="/dashboard/agent-requests" element={<AgentRequestQueueMain />} />
          <Route path="/dashboard/carrier-change-requests" element={<CarrierChangeRequestQueueMain />} />
          <Route path="/dashboard/users" element={<UsersMain />} />
          <Route path="/dashboard/services" element={<ServicesOpsMain />} />
          <Route path="/dashboard/feature-flags" element={<FeatureFlagsMain />} />
          <Route path="/dashboard/carriers" element={<CarriersMain />} />
          <Route path="/dashboard/inbox" element={<InboxMain />} />
          <Route path="/dashboard/onboarding-tasks" element={<OnboardingTasksMain />} />
          <Route path="/dashboard/account" element={<StaffAccountMain />} />
          <Route path="/agent/:agentId" element={<Navigate to="/my-agent/downlines" replace />} />
          <Route
            path="/dashboard/submissions"
            element={
              <StaffOnlyMain>
                <DashboardMain />
              </StaffOnlyMain>
            }
          />
          <Route
            path="/dashboard/submissions/:submissionId"
            element={
              <StaffOnlyMain>
                <DashboardMain />
              </StaffOnlyMain>
            }
          />
          <Route
            path="/run-artifacts"
            element={
              <StaffOnlyMain>
                <RunArtifactsMain />
              </StaffOnlyMain>
            }
          />
          <Route
            path="/sms-uplines"
            element={
              <StaffOnlyMain>
                <SmsUplinesMain />
              </StaffOnlyMain>
            }
          />
          <Route
            path="/dashboard/sync-health"
            element={
              <StaffOnlyMain>
                <SyncHealthMain />
              </StaffOnlyMain>
            }
          />
          <Route path="/reagan-data" element={<Navigate to="/dashboard/sync-health" replace />} />
          <Route path="/pinnacle-data" element={<Navigate to="/dashboard/sync-health" replace />} />
          <Route path="/ppg/agents" element={<PPGAgentsRedirect />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}
